'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { motion, type Variants } from 'motion/react';
import { getVariants, useAnimateIconContext, IconWrapper, type IconProps } from '@/components/animate-ui/icons/icon';
type SlidersHorizontalProps = IconProps<keyof typeof animations>;
const animations = {
  default: {
    line1: {
      initial: {
        x2: 10
      },
      animate: {
        x2: 4,
        transition: {
          ease: 'easeInOut',
          duration: 0.4
        }
      }
    },
    line2: {
      initial: {
        x1: 14,
        x2: 14
      },
      animate: {
        x1: 8,
        x2: 8,
        transition: {
          ease: 'easeInOut',
          duration: 0.4
        }
      }
    },
    line3: {
      initial: {
        x1: 14
      },
      animate: {
        x1: 8,
        transition: {
          ease: 'easeInOut',
          duration: 0.4
        }
      }
    },
    line4: {
      initial: {
        x2: 8
      },
      animate: {
        x2: 16,
        transition: {
          ease: 'easeInOut',
          duration: 0.4
        }
      }
    },
    line5: {
      initial: {
        x1: 8,
        x2: 8
      },
      animate: {
        x1: 16,
        x2: 16,
        transition: {
          ease: 'easeInOut',
          duration: 0.4
        }
      }
    },
    line6: {
      initial: {
        x1: 12
      },
      animate: {
        x1: 20,
        transition: {
          ease: 'easeInOut',
          duration: 0.4
        }
      }
    },
    line7: {
      initial: {
        x2: 12
      },
      animate: {
        x2: 7,
        transition: {
          ease: 'easeInOut',
          duration: 0.4
        }
      }
    },
    line8: {
      initial: {
        x1: 16,
        x2: 16
      },
      animate: {
        x1: 11,
        x2: 11,
        transition: {
          ease: 'easeInOut',
          duration: 0.4
        }
      }
    },
    line9: {
      initial: {
        x1: 16
      },
      animate: {
        x1: 11,
        transition: {
          ease: 'easeInOut',
          duration: 0.4
        }
      }
    }
  } satisfies Record<string, Variants>,
  'default-loop': {
    line1: {
      initial: {
        x2: 10
      },
      animate: {
        x2: [10, 4, 10],
        transition: {
          ease: 'easeInOut',
          duration: 0.8
        }
      }
    },
    line2: {
      initial: {
        x1: 14,
        x2: 14
      },
      animate: {
        x1: [14, 8, 14],
        x2: [14, 8, 14],
        transition: {
          ease: 'easeInOut',
          duration: 0.8
        }
      }
    },
    line3: {
      initial: {
        x1: 14
      },
      animate: {
        x1: [14, 8, 14],
        transition: {
          ease: 'easeInOut',
          duration: 0.8
        }
      }
    },
    line4: {
      initial: {
        x2: 8
      },
      animate: {
        x2: [8, 16, 8],
        transition: {
          ease: 'easeInOut',
          duration: 0.8
        }
      }
    },
    line5: {
      initial: {
        x1: 8,
        x2: 8
      },
      animate: {
        x1: [8, 16, 8],
        x2: [8, 16, 8],
        transition: {
          ease: 'easeInOut',
          duration: 0.8
        }
      }
    },
    line6: {
      initial: {
        x1: 12
      },
      animate: {
        x1: [12, 20, 12],
        transition: {
          ease: 'easeInOut',
          duration: 0.8
        }
      }
    },
    line7: {
      initial: {
        x2: 12
      },
      animate: {
        x2: [12, 7, 12],
        transition: {
          ease: 'easeInOut',
          duration: 0.8
        }
      }
    },
    line8: {
      initial: {
        x1: 16,
        x2: 16
      },
      animate: {
        x1: [16, 11, 16],
        x2: [16, 11, 16],
        transition: {
          ease: 'easeInOut',
          duration: 0.8
        }
      }
    },
    line9: {
      initial: {
        x1: 16
      },
      animate: {
        x1: [16, 11, 16],
        transition: {
          ease: 'easeInOut',
          duration: 0.8
        }
      }
    }
  } satisfies Record<string, Variants>
} as const;
function IconComponent(t0) {
  const $ = _c(26);
  let props;
  let size;
  if ($[0] !== t0) {
    ({
      size,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = size;
  } else {
    props = $[1];
    size = $[2];
  }
  const {
    controls
  } = useAnimateIconContext();
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getVariants(animations);
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const variants = t1;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== controls) {
    t2 = <motion.line x1="3" y1="5" x2="10" y2="5" variants={variants.line1} initial="initial" animate={controls} />;
    t3 = <motion.line x1="14" y1="3" x2="14" y2="7" variants={variants.line2} initial="initial" animate={controls} />;
    t4 = <motion.line x1="14" y1="5" x2="21" y2="5" variants={variants.line3} initial="initial" animate={controls} />;
    t5 = <motion.line x1="3" y1="12" x2="8" y2="12" variants={variants.line4} initial="initial" animate={controls} />;
    t6 = <motion.line x1="8" y1="10" x2="8" y2="14" variants={variants.line5} initial="initial" animate={controls} />;
    t7 = <motion.line x1="12" y1="12" x2="21" y2="12" variants={variants.line6} initial="initial" animate={controls} />;
    t8 = <motion.line x1="3" y1="19" x2="12" y2="19" variants={variants.line7} initial="initial" animate={controls} />;
    t9 = <motion.line x1="16" y1="17" x2="16" y2="21" variants={variants.line8} initial="initial" animate={controls} />;
    t10 = <motion.line x1="16" y1="19" x2="21" y2="19" variants={variants.line9} initial="initial" animate={controls} />;
    $[4] = controls;
    $[5] = t10;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t10 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
    t9 = $[13];
  }
  let t11;
  if ($[14] !== props || $[15] !== size || $[16] !== t10 || $[17] !== t2 || $[18] !== t3 || $[19] !== t4 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
    t11 = <motion.svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}</motion.svg>;
    $[14] = props;
    $[15] = size;
    $[16] = t10;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
}
function SlidersHorizontal(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <IconWrapper icon={IconComponent} {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export { animations, SlidersHorizontal, SlidersHorizontal as SlidersHorizontalIcon, type SlidersHorizontalProps, type SlidersHorizontalProps as SlidersHorizontalIconProps };